import { useRef } from 'react';
import { C, soft } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ─────────────────────────────────────────────────────────────────────────────
// UI language picker.
//
// Built like ThemePicker (its sibling in this folder) and for the same reasons:
// a real WAI-ARIA radiogroup, arrow keys move and select, only the selected
// option is in the tab order, and the selection is carried by a checkmark and a
// ring as well as by colour.
//
// Two rules specific to this control:
//
//   • Each option is labelled IN ITS OWN LANGUAGE. Someone who opens this screen
//     because the app is in a language they cannot read has to be able to find
//     their own — «English» must say English even while the UI is Persian. The
//     option is therefore forced to its own direction too, so a Persian name
//     inside an LTR layout still reads correctly.
//
//   • Focusing an option preloads its catalogue. By the time the doctor decides,
//     the strings are cached, so the switch itself is synchronous.
// ─────────────────────────────────────────────────────────────────────────────

export default function LanguagePicker({ value, onChange, testId = 'settings-language-picker' }) {
  const { t, locales, preloadLocale } = useTranslation();
  const refs = useRef({});

  const options = Object.values(locales);
  const isRTLLayout = document?.documentElement?.getAttribute('dir') === 'rtl';

  const onKeyDown = (e) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();

    const i = options.findIndex((o) => o.id === value);
    const current = i === -1 ? 0 : i;
    // The arrows follow what the doctor SEES, so which one advances depends on
    // the direction the list is currently laid out in — not on the array order.
    const forward = isRTLLayout
      ? (e.key === 'ArrowLeft' || e.key === 'ArrowDown')
      : (e.key === 'ArrowRight' || e.key === 'ArrowDown');
    const step = forward ? 1 : -1;

    let next;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = options.length - 1;
    else next = (current + step + options.length) % options.length;

    const id = options[next].id;
    onChange(id);
    refs.current[id]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('settings.language.pickerLabel')}
      data-testid={testId}
      onKeyDown={onKeyDown}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
        gap: 12,
      }}
    >
      {options.map((option) => {
        const selected = option.id === value;
        return (
          <button
            key={option.id}
            ref={(el) => { refs.current[option.id] = el; }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            data-testid={`${testId}-option-${option.id}`}
            onClick={() => onChange(option.id)}
            onFocus={() => preloadLocale(option.id)}
            onMouseEnter={() => preloadLocale(option.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              minHeight: 56, padding: '10px 14px',
              borderRadius: 10, cursor: 'pointer', textAlign: 'start',
              background: selected ? soft(C.navy, 0.06) : C.card,
              border: `1.5px solid ${selected ? C.navy : C.border}`,
              boxShadow: selected ? `0 0 0 3px ${soft(C.navy, 0.13)}` : 'none',
              transition: 'border-color .15s, box-shadow .15s, background .15s',
            }}
          >
            {/* The tag itself — an unambiguous, script-independent handle on the
                option, and the thing a screenshot in a bug report will show. */}
            <span
              aria-hidden
              dir="ltr"
              style={{
                width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                display: 'grid', placeItems: 'center',
                background: selected ? C.navy : soft(C.navy, 0.08),
                color: selected ? '#fff' : C.navy,
                fontSize: 12, fontWeight: 800, letterSpacing: 0.5,
                textTransform: 'uppercase',
              }}
            >
              {option.id}
            </span>

            {/* Always in the option's OWN direction, so a Persian name inside an
                English layout (and vice versa) still reads the right way round. */}
            <span dir={option.dir} style={{ flex: 1, minWidth: 0, textAlign: 'start' }}>
              <span style={{
                display: 'block', fontSize: 14,
                fontWeight: selected ? 700 : 600,
                color: selected ? C.textPrimary : C.textSecondary,
              }}>
                {option.label}
              </span>
              <span style={{ display: 'block', fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                {t(`settings.language.names.${option.id}`)}
              </span>
            </span>

            {/* Redundant with the ring on purpose — colour is never the only
                carrier of the selected state. */}
            {selected && (
              <svg viewBox="0 0 24 24" width={16} height={16} fill={C.navy} aria-hidden style={{ flexShrink: 0 }}>
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}
